/**
 * Leprendo Product Module
 * Handles variant selection, gallery switching, selling plan toggle, and add-to-cart.
 */

(function () {
  'use strict';

  class ProductPage {
    constructor(form) {
      this.form = form;
      this.root = form.closest('[data-product-root]') || form;
      this.variantData = JSON.parse(form.querySelector('[data-variant-json]')?.textContent || '[]');
      this.currentVariant = this.variantData.find(v => v.available) || this.variantData[0];

      this.mainMediaContainer = this.root.querySelector('.product-gallery__main');
      this.mainImage = this.root.querySelector('[data-main-image]');
      this.priceEl = this.root.querySelector('[data-product-price]');
      this.comparePriceEl = this.root.querySelector('[data-compare-price]');
      this.addBtn = this.form.querySelector('[data-add-to-cart]');
      this.variantInput = this.form.querySelector('input[name="id"]');
      this.sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
      this.selectedOptions = {};

      this.initVariantPicker();
      this.initGallery();
      this.initSellingPlans();
      this.initAccordions();
    }

    initVariantPicker() {
      const optionEls = this.form.querySelectorAll('[data-option-name]');
      optionEls.forEach(el => {
        const optionName = el.dataset.optionName;
        this.selectedOptions[optionName] = el.dataset.optionValue;
      });

      this.form.querySelectorAll('.variant-option').forEach(btn => {
        btn.addEventListener('click', () => {
          const name = btn.dataset.optionName;
          const value = btn.dataset.optionValue;

          // Deselect siblings
          this.form.querySelectorAll(`.variant-option[data-option-name="${name}"]`)
            .forEach(b => b.classList.remove('active'));

          btn.classList.add('active');
          this.selectedOptions[name] = value;
          this.updateVariant();
        });
      });
    }

    updateVariant() {
      const selectedValues = Object.values(this.selectedOptions);
      const matched = this.variantData.find(v =>
        v.options.every((opt, i) => opt === selectedValues[i])
      );
      if (!matched) return;

      this.currentVariant = matched;

      // Update hidden input
      if (this.variantInput) this.variantInput.value = matched.id;

      // Update price
      this.updatePrice(matched);

      // Update add button
      this.updateAddButton(matched);

      // Update URL
      const url = new URL(window.location.href);
      url.searchParams.set('variant', matched.id);
      window.history.replaceState({}, '', url.toString());

      // Update image if variant has one
      if (matched.featured_media?.preview_image?.src) {
        const thumb = this.root.querySelector(`[data-media-id="${matched.featured_media.id}"]`);
        if (thumb && thumb.dataset.mediaType === 'video' && thumb.dataset.videoSrc) {
          this.switchMainVideo(thumb.dataset.videoSrc);
          this.setActiveThumb(thumb);
        } else {
          this.switchMainImage(matched.featured_media.preview_image.src, matched.featured_media.alt || '');
          if (thumb) this.setActiveThumb(thumb);
        }
      }
    }

    updatePrice(variant) {
      if (!this.priceEl) return;
      const price = (variant.price / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      this.priceEl.textContent = price;

      if (this.comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const compare = (variant.compare_at_price / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
          this.comparePriceEl.textContent = compare;
          this.comparePriceEl.style.display = '';
          this.priceEl.classList.add('sale-price');
        } else {
          this.comparePriceEl.style.display = 'none';
          this.priceEl.classList.remove('sale-price');
        }
      }
    }

    updateAddButton(variant) {
      if (!this.addBtn) return;
      if (variant.available) {
        this.addBtn.disabled = false;
        this.addBtn.textContent = 'Add to Cart';
      } else {
        this.addBtn.disabled = true;
        this.addBtn.textContent = 'Sold Out';
      }
    }

    initGallery() {
      const thumbs = this.root.querySelectorAll('.product-gallery__thumb');
      thumbs.forEach(thumb => {
        const activateThumb = () => {
          const mediaType = thumb.dataset.mediaType;
          const src = thumb.dataset.imageSrc;
          const alt = thumb.dataset.imageAlt || '';

          if (mediaType === 'video' && thumb.dataset.videoSrc) {
            this.switchMainVideo(thumb.dataset.videoSrc);
          } else {
            this.switchMainImage(src, alt);
          }

          this.setActiveThumb(thumb);
        };

        thumb.addEventListener('click', activateThumb);
        thumb.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            activateThumb();
          }
        });
      });
    }

    switchMainImage(src, alt) {
      if (!this.mainMediaContainer || !src) return;
      if (!this.mainImage) {
        this.mainMediaContainer.replaceChildren();
        const image = document.createElement('img');
        image.id = 'main-product-image';
        image.setAttribute('data-main-image', '');
        image.src = src;
        image.alt = alt;
        image.width = 900;
        image.height = 900;
        image.loading = 'eager';
        this.mainMediaContainer.appendChild(image);
        this.mainImage = image;
        return;
      }
      this.mainImage.src = src;
      this.mainImage.alt = alt;
    }

    switchMainVideo(src) {
      if (!this.mainMediaContainer || !src) return;
      this.mainMediaContainer.replaceChildren();
      const video = document.createElement('video');
      video.className = 'product-gallery__video';
      video.src = src;
      video.controls = true;
      video.autoplay = true;
      video.muted = true;
      video.playsInline = true;
      this.mainMediaContainer.appendChild(video);
      this.mainImage = null;

      video.play().catch(() => {});
    }

    setActiveThumb(thumb) {
      this.root.querySelectorAll('.product-gallery__thumb').forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
    }

    initSellingPlans() {
      const options = this.form.querySelectorAll('.selling-plan-option input');
      const planSelect = this.form.querySelector('[data-selling-plan-select]');

      options.forEach(input => {
        input.addEventListener('change', () => {
          const planId = input.value;
          if (planId === 'one-time') {
            if (this.sellingPlanInput) this.sellingPlanInput.value = '';
            if (planSelect) planSelect.style.display = 'none';
          } else {
            if (this.sellingPlanInput) this.sellingPlanInput.value = planId;
            if (planSelect) planSelect.style.display = '';
          }
        });
      });

      if (planSelect) {
        planSelect.addEventListener('change', () => {
          if (this.sellingPlanInput) this.sellingPlanInput.value = planSelect.value;
        });
      }
    }

    initAccordions() {
      this.root.querySelectorAll('.accordion-btn').forEach(trigger => {
        trigger.addEventListener('click', () => {
          const expanded = trigger.getAttribute('aria-expanded') === 'true';
          trigger.setAttribute('aria-expanded', String(!expanded));
          const content = trigger.nextElementSibling;
          if (content) content.classList.toggle('open', !expanded);
        });
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    const productContainer = document.querySelector('[data-product-container]');
    if (productContainer) new ProductPage(productContainer);
  });
})();
